import React, { memo } from "react";
import clsx from "clsx";
import "../../scss/privacy_flow_modal.scss";
import { PrivacyGhost } from "./PrivacyGhost";

// ============================================
// ICON COMPONENTS
// ============================================

// Redacted Ghost Logo (purple see-through gradient)
const GhostIcon = memo((props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 1210 1493" fill="none" {...props}>
    <defs>
      <linearGradient
        id="ghostGradientOnboarding"
        x1="605"
        y1="0"
        x2="605"
        y2="1493"
        gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#D615EB" stopOpacity={1} />
        <stop offset="100%" stopColor="#8436F5" stopOpacity={0.15} />
      </linearGradient>
    </defs>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M568.97 0.518916C515.968 2.82316 482.805 6.22945 449.241 12.9418C269.897 48.7077 151.57 165.423 81.4365 376.011C41.0593 497.535 13.3057 654.124 3.88742 813.816C-0.921555 896.266 -1.3225 992.447 2.98609 1064.28C7.99554 1150.14 19.0168 1229.59 33.4445 1285.69C47.3711 1339.79 70.7159 1387.77 96.0647 1414.83C129.729 1450.59 166.199 1457.61 209.682 1436.57C228.317 1427.55 240.141 1418.23 272.702 1387.27C308.471 1353.11 325.103 1342.89 346.244 1341.49C356.964 1340.89 365.18 1342.49 375.099 1347.3C391.13 1355.12 402.752 1366.03 431.306 1400.4C454.651 1428.35 464.47 1437.57 488.316 1453.69C516.77 1473.03 545.726 1485.26 575.984 1490.76C591.614 1493.57 620.967 1493.77 635.597 1491.17C666.36 1485.66 693.209 1474.73 720.758 1456.7C747.814 1439.07 766.652 1421.33 788.394 1393.39C804.52 1372.55 822.857 1353.71 831.87 1348.3C849.507 1337.98 868.146 1337.48 888.384 1346.9C903.308 1353.71 914.629 1362.73 943.888 1390.68C965.933 1411.72 976.855 1421.04 988.876 1429.05C1001.2 1437.27 1013.52 1442.98 1025.65 1446.08C1034.46 1448.39 1036.77 1448.59 1049.89 1448.19C1066.62 1447.69 1073.03 1446.08 1086.96 1438.87C1124.23 1419.73 1154.79 1370.14 1175.94 1294.2C1193.37 1231.89 1202.09 1167.57 1208.29 1056.77C1210 1026.21 1210.6 904.786 1209.29 867.82C1198.27 565.861 1137.15 325.017 1035.57 183.255C1011.82 150.295 983.967 120.039 957.516 98.8001C874.558 32.0767 757.026 -0.88366 605.542 0.0180072C589.008 0.118157 572.577 0.318536 568.97 0.518916ZM459.26 340.345C497.634 348.26 525.186 376.011 533.402 414.782C536.007 427.005 537.109 453.453 537.109 503.746C537.109 570.57 534.705 611.645 529.695 627.775C526.99 636.791 521.178 648.112 515.468 655.727C489.919 689.689 438.621 700.609 396.94 681.073C367.384 667.248 350.151 643.604 346.645 611.845C344.44 592.109 343.338 544.02 343.839 494.73C344.741 415.484 346.645 401.458 358.467 383.325C370.089 365.592 389.827 349.763 409.164 342.75C421.688 338.241 443.83 337.139 459.26 340.345ZM790.391 340.045C832.578 347.959 862.132 380.419 867.438 424.501C870.644 450.248 870.644 541.015 867.541 581.59C865.036 614.751 862.434 628.075 856.022 641.501C841.09 672.457 809.834 691.092 772.857 691.092C733.288 691.092 698.515 669.151 684.792 635.489C678.078 618.958 676.773 599.222 676.678 515.768C676.678 418.289 678.38 402.76 691.101 383.325C705.229 361.584 729.175 344.654 752.325 339.944C761.442 338.141 780.479 338.141 790.391 340.045Z"
      fill="url(#ghostGradientOnboarding)"
    />
  </svg>
));
GhostIcon.displayName = "GhostIcon";

const XmarkIcon = memo((props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    {...props}>
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
));
XmarkIcon.displayName = "XmarkIcon";

const CheckIcon = memo((props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    {...props}>
    <path d="M5 12l5 5L20 7" />
  </svg>
));
CheckIcon.displayName = "CheckIcon";

const SignatureIcon = memo((props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...props}>
    <path d="M17 3a2.85 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
));
SignatureIcon.displayName = "SignatureIcon";

const ExternalLinkIcon = memo((props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...props}>
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
    <path d="M15 3h6v6" />
    <path d="M10 14L21 3" />
  </svg>
));
ExternalLinkIcon.displayName = "ExternalLinkIcon";

// ============================================
// STEP DATA
// ============================================

interface StepData {
  number: number;
  title: string;
  description: string;
}

const STEPS: StepData[] = [
  {
    number: 1,
    title: "Generate Secret key",
    description: "Derives your secret key from your wallet.",
  },
  {
    number: 2,
    title: "Register Your Account",
    description:
      "Creates your Redacted identity within the Private Reserve. Links your wallet address to an encrypted account.",
  },
  {
    number: 3,
    title: "Create Private Account",
    description:
      "Sets up your private Spending account. Needed to interact with DeFi privately from your Spending balance.",
  },
];

// ============================================
// STEP COMPONENT
// ============================================

type StepStatus = "pending" | "active" | "signing" | "completed";

interface StepProps {
  step: StepData;
  status: StepStatus;
  onSign?: () => void;
}

const Step: React.FC<StepProps> = ({ step, status, onSign }) => {
  const isClickable = status === "active";
  const isSigning = status === "signing";
  const isCompleted = status === "completed";
  const isPending = status === "pending";

  return (
    <div
      className={clsx(
        "private-onboarding__step",
        status === "active" && "private-onboarding__step--active",
        status === "signing" && "private-onboarding__step--signing",
        status === "completed" && "private-onboarding__step--completed",
        status === "pending" && "private-onboarding__step--pending"
      )}>
      {/* Step Number / Status Indicator */}
      <div className="private-onboarding__step-indicator">
        {isCompleted ? (
          <CheckIcon className="private-onboarding__step-check" />
        ) : (
          <span className="private-onboarding__step-number">{step.number}</span>
        )}
      </div>

      {/* Step Content */}
      <div className="private-onboarding__step-content">
        <div className="private-onboarding__step-header">
          <span className="private-onboarding__step-title">{step.title}</span>
          {!isCompleted && (
            <span className="private-onboarding__step-badge">
              <SignatureIcon className="private-onboarding__step-badge-icon" />
              Requires signature
            </span>
          )}
          {isCompleted && (
            <span className="private-onboarding__step-badge private-onboarding__step-badge--completed">
              <CheckIcon className="private-onboarding__step-badge-icon" />
              Complete
            </span>
          )}
        </div>
        <p className="private-onboarding__step-description">
          {step.description}
        </p>
      </div>

      {/* Action Button */}
      <div className="private-onboarding__step-action">
        <button
          className={clsx(
            "private-onboarding__step-button",
            isCompleted && "private-onboarding__step-button--completed",
            isSigning && "private-onboarding__step-button--signing",
            isPending && "private-onboarding__step-button--disabled"
          )}
          onClick={onSign}
          disabled={!isClickable}>
          {isSigning ? (
            <>
              <PrivacyGhost
                motion="swirl"
                className="private-onboarding__step-button-loader filter-white"
              />
              Signing...
            </>
          ) : isCompleted ? (
            <>
              <CheckIcon className="private-onboarding__step-button-icon" />
              Done
            </>
          ) : (
            <>Sign</>
          )}
        </button>
      </div>
    </div>
  );
};

// ============================================
// MAIN ONBOARDING MODAL COMPONENT
// ============================================

interface PrivateModeOnboardingProps {
  /** Current step (1, 2, or 3) */
  currentStep?: number;
  /** Array of completed step numbers */
  completedSteps?: number[];
  /** Whether currently signing */
  isSigning?: boolean;
  /** Callback when user clicks sign on a step */
  onSign?: (stepNumber: number) => void;
  /** Callback when modal is closed */
  onClose?: () => void;
  /** Link to documentation */
  docsUrl?: string;
  /** Additional class name */
  className?: string;
  /** View mode: 'initial' shows intro, 'signing' shows step-by-step progress */
  viewMode?: "initial" | "signing";
}

export const PrivateModeOnboarding: React.FC<PrivateModeOnboardingProps> = ({
  currentStep = 1,
  completedSteps = [],
  isSigning = false,
  onSign,
  onClose,
  docsUrl = "https://docs.redacted.money",
  className,
}) => {
  const getStepStatus = (stepNumber: number): StepStatus => {
    if (completedSteps.includes(stepNumber)) return "completed";
    if (stepNumber === currentStep && isSigning) return "signing";
    if (stepNumber === currentStep) return "active";
    return "pending";
  };

  const allCompleted = completedSteps.length === 3;

  return (
    <div className={clsx("private-onboarding-modal", className)}>
      {/* Modal Window */}
      <div className="private-onboarding-modal__window">
        {/* Header */}
        <div className="private-onboarding-modal__header">
          <div className="private-onboarding-modal__header-content">
            <div className="private-onboarding-modal__logo">
              <GhostIcon className="private-onboarding-modal__ghost" />
            </div>
            <div className="private-onboarding-modal__titles">
              <h2 className="private-onboarding-modal__title">Private Mode</h2>
              <span className="private-onboarding-modal__subtitle">
                by Redacted
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="private-onboarding-modal__content">
          {/* Introduction Text */}
          <p className="private-onboarding-modal__intro">
            {allCompleted
              ? "Privacy mode is now active. Your DeFi interactions are protected."
              : "Complete 3 transactions to enable privacy protection for your wallet."}
          </p>

          {/* Steps */}
          <div className="private-onboarding__steps">
            {STEPS.map((step) => (
              <Step
                key={step.number}
                step={step}
                status={getStepStatus(step.number)}
                onSign={() => onSign?.(step.number)}
              />
            ))}
          </div>

          {/* After Setup Note */}
          {!allCompleted && (
            <div className="private-onboarding__note">
              <div className="private-onboarding__note-label">
                After initial setup
              </div>
              <p className="private-onboarding__note-text">
                Future logins only require two signatures to unlock your private
                account.
              </p>
            </div>
          )}

          {/* Success Message */}
          {allCompleted && (
            <div className="private-onboarding__success">
              <div className="private-onboarding__success-icon">
                <GhostIcon className="private-onboarding__success-ghost" />
              </div>
              <div className="private-onboarding__success-content">
                <span className="private-onboarding__success-title">
                  Privacy Mode Active
                </span>
                <p className="private-onboarding__success-text">
                  Your deposits enter a shared privacy set, breaking on-chain
                  links. Trade, borrow, and stake privately.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="private-onboarding-modal__footer">
          {/* Docs Link */}
          <a
            href={docsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="private-onboarding-modal__docs-link">
            <ExternalLinkIcon className="private-onboarding-modal__docs-icon" />
            Learn more about Redacted
          </a>
          {allCompleted && (
            <div className="private-onboarding-modal__actions">
              <button
                className="private-onboarding-modal__button private-onboarding-modal__button--primary"
                onClick={onClose}>
                Start Using Private Mode
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrivateModeOnboarding;
